import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth, can } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, Briefcase, Users, ClipboardList, Package, ShoppingCart,
  Wrench, UsersRound, CalendarDays, BarChart3, Settings, Search, Bell,
  Sun, Moon, LogOut, Menu, X, TrendingUp, Shield, Truck, Car, Smartphone, Sparkles, Rocket, LifeBuoy, MapPin, BookOpen, Receipt, FolderKanban, BadgeCheck, HardHat, FolderOpen
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";

const NAV = [
  { to: "/my-work", label: "My Work", icon: LayoutDashboard, end: true },
  { to: "/field", label: "My Day (Field)", icon: Smartphone, module: "jobs", orgModule: "field_mobile" },
  { to: "/command", label: "Command Centre", icon: TrendingUp, module: "reports", orgModule: "reports" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, module: "jobs" },
  { to: "/projects", label: "Projects", icon: FolderKanban, module: "jobs", orgModule: "projects" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, module: "scheduling", orgModule: "scheduling" },
  { to: "/tasks", label: "Tasks", icon: ClipboardList, module: "tasks", orgModule: "tasks" },
  { to: "/customers", label: "Customers", icon: Users, module: "customers" },
  { to: "/properties", label: "Properties", icon: MapPin, module: "customers", orgModule: "properties" },
  { to: "/vehicles", label: "Vehicles", icon: Car, orgModule: "customer_vehicles" },
  { to: "/documents", label: "Documents", icon: FolderOpen },
  { to: "/leads", label: "Leads & Quotes", icon: TrendingUp, module: "leads", orgModule: "quotes" },
  { to: "/price-book", label: "Price Book", icon: BookOpen, module: "quotes", orgModule: "price_book" },
  { to: "/invoices", label: "Invoices", icon: Receipt, module: "quotes", orgModule: "invoicing" },
  { to: "/inventory", label: "Inventory", icon: Package, module: "inventory", orgModule: "inventory" },
  { to: "/purchasing", label: "Purchasing", icon: ShoppingCart, module: "purchasing", orgModule: "purchasing" },
  { to: "/receiving", label: "Receiving", icon: Truck, module: "receiving", orgModule: "purchasing" },
  { to: "/equipment", label: "Equipment", icon: Wrench, module: "equipment", orgModule: "equipment" },
  { to: "/fleet", label: "Fleet", icon: Car, module: "fleet", orgModule: "fleet" },
  { to: "/agreements", label: "Service Agreements", icon: BadgeCheck, module: "customers", orgModule: "service_agreements" },
  { to: "/safety", label: "Safety", icon: HardHat, module: "jobs", orgModule: "safety" },
  { to: "/people", label: "People & Crews", icon: UsersRound, module: "people" },
  { to: "/reports", label: "Reports", icon: BarChart3, module: "reports", orgModule: "reports" },
  { to: "/setup", label: "Setup Portal", icon: Rocket, module: "settings" },
  { to: "/settings", label: "Settings", icon: Settings, module: "settings" },
  { to: "/support", label: "Support", icon: LifeBuoy },
];

function AiAssistant() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);

  const ask = async () => {
    if (!q.trim() || busy) return;
    const question = q.trim();
    setQ("");
    setMessages((m) => [...m, { role: "user", content: question }]);
    setBusy(true);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/ask`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value).split("\n")) {
          if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.delta) {
              acc += payload.delta;
              const snap = acc;
              setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: snap }]);
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "Assistant unavailable right now." }]);
    }
    setBusy(false);
  };

  const SUGGESTIONS = ["What's holding up work this week?", "Which jobs are blocked?",
    "Summarize today's operations", "Which suppliers have late deliveries?"];

  return (
    <>
      <button data-testid="ai-assistant-fab" onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-40 h-12 w-12 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 shadow-lg flex items-center justify-center transition-transform hover:scale-105">
        <Sparkles className="h-5 w-5" />
      </button>
      {open && (
        <div data-testid="ai-assistant-panel"
          className="fixed bottom-20 right-5 z-40 w-[calc(100vw-2.5rem)] max-w-sm h-[480px] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl flex flex-col overflow-hidden animate-fade-up">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <span className="font-heading font-bold text-sm">Operations Assistant</span>
            <span className="text-[10px] text-slate-400">AI assists - humans decide</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3 scrollbar-thin">
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Ask about your operations. Answers use only data you can access.</p>
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid={`ai-suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                    onClick={() => setQ(s)}
                    className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-950/30 text-slate-600 dark:text-slate-300">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`text-sm px-3 py-2 rounded-xl max-w-[90%] whitespace-pre-wrap ${
                m.role === "user" ? "bg-amber-500 text-slate-900 ml-auto"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200"}`}>
                {m.content || (busy && i === messages.length - 1 ? "..." : "")}
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
            <Input data-testid="ai-question-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask a question..." className="h-9" />
            <Button data-testid="ai-ask-button" onClick={ask} disabled={busy || !q.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-9">Ask</Button>
          </div>
        </div>
      )}
    </>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    if (v.length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(v)}`);
        setResults(data.results);
        setOpen(true);
      } catch { setResults([]); }
    }, 250);
  };

  return (
    <div className="relative flex-1 max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input data-testid="global-search-input" value={q}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search jobs, customers, POs, materials..."
        className="pl-9 bg-slate-800/60 border-slate-700 text-slate-200 placeholder:text-slate-500 focus-visible:ring-amber-500" />
      {open && results.length > 0 && (
        <div data-testid="global-search-results"
          className="absolute top-11 left-0 right-0 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {results.map((r) => (
            <button key={`${r.kind}-${r.id}`}
              data-testid={`search-result-${r.kind.toLowerCase()}-${r.id}`}
              onMouseDown={() => { navigate(r.link); setOpen(false); setQ(""); }}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 text-slate-800 dark:text-slate-200">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 w-20 shrink-0">{r.kind}</span>
              <span className="truncate">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationPrefs() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    if (open) api.get("/notification-preferences").then(({ data }) => setPrefs(data)).catch(() => {});
  }, [open]);
  const set = async (patch) => {
    setPrefs({ ...prefs, ...patch });
    await api.put("/notification-preferences", patch).catch(() => {});
  };
  if (!prefs && !open) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-preferences-button"
          className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 font-medium">
          Preferences
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700" align="end">
        {prefs && (
          <div className="space-y-3 text-sm">
            {[["in_app", "In-app notifications"], ["critical_only", "Critical only"],
              ["daily_summary", "Daily summary"], ["email_future", "Email (coming soon)"],
              ["sms_future", "SMS (coming soon)"]].map(([k, label]) => (
              <label key={k} className="flex items-center justify-between cursor-pointer">
                <span className="text-slate-700 dark:text-slate-300">{label}</span>
                <input type="checkbox" data-testid={`pref-${k.replace(/_/g, "-")}`}
                  checked={!!prefs[k]} onChange={(e) => set({ [k]: e.target.checked })}
                  className="accent-amber-500 h-4 w-4" />
              </label>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const unread = items.filter((n) => !n.read).length;
  const openAll = async () => { await api.post("/notifications/read", { ids: [] }); load(); };
  const dismiss = async (n, e) => {
    e.stopPropagation();
    await api.post(`/notifications/${n.id}/dismiss`).catch(() => {});
    load();
  };
  const CATEGORY_LABELS = { automation: "Automation", issue_reported: "Issue",
    approval_required: "Approval", completion_review: "Review",
    customer_confirmation: "Customer", material_problem: "Material", general: "" };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="notifications-popover-trigger"
          className="relative text-slate-300 hover:text-white hover:bg-slate-800">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notifications-unread-count"
              className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <span className="font-semibold text-sm font-heading">Notifications</span>
          <div className="flex items-center gap-3">
            <NotificationPrefs />
            <button onClick={openAll} data-testid="notifications-mark-read"
              className="text-xs text-amber-600 dark:text-amber-400 font-medium">Mark all read</button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {items.length === 0 && <div className="p-4 text-sm text-slate-500">No notifications</div>}
          {items.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`}
              className={`group w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 ${!n.read ? "bg-amber-50/60 dark:bg-amber-950/20" : ""}`}>
              <div className="flex items-start gap-2">
                <button className="flex-1 min-w-0 text-left" onClick={() => n.link && navigate(n.link)}>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{n.title}</div>
                  {n.body && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>}
                  {CATEGORY_LABELS[n.category] && (
                    <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">{CATEGORY_LABELS[n.category]}</span>
                  )}
                </button>
                <button onClick={(e) => dismiss(n, e)} data-testid={`notification-dismiss-${n.id}`}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 shrink-0">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return (
    <Button variant="ghost" size="icon" data-testid="theme-toggle-button"
      onClick={() => setDark(!dark)}
      className="text-slate-300 hover:text-white hover:bg-slate-800">
      {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
}

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  const modules = user?.org?.modules || {};
  const items = NAV.filter((n) => {
    if (n.orgModule && !modules[n.orgModule]) return false;
    if (n.module && !can(user, n.module, "view")) return false;
    return true;
  });
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
          data-testid={`navigation-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? "bg-amber-500 text-slate-900"
                : "text-slate-400 hover:text-white hover:bg-slate-800"
            }`}>
          <n.icon className="h-4 w-4 shrink-0" />
          {n.label}
        </NavLink>
      ))}
      {user?.permissions?.platform && (
        <NavLink to="/platform" data-testid="navigation-link-platform-admin"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-amber-500 text-slate-900" : "text-slate-400 hover:text-white hover:bg-slate-800"}`}>
          <Shield className="h-4 w-4" /> Platform Admin
        </NavLink>
      )}
    </nav>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const term = user?.org?.terminology || {};

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
        <div className="h-8 w-8 rounded-lg bg-amber-500 flex items-center justify-center">
          <Briefcase className="h-4.5 w-4.5 h-5 w-5 text-slate-900" />
        </div>
        <div className="min-w-0">
          <div className="font-heading font-bold text-white text-sm leading-tight truncate">
            {user?.org?.name || "Fieldistry"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            {user?.org?.industry_label || "Operations"}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-4 scrollbar-thin">
        <NavItems onNavigate={() => setMobileOpen(false)} />
      </div>
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-amber-400">
            {(user?.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-white truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.role_name || "Platform"}</div>
          </div>
          <Button variant="ghost" size="icon" data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-slate-400 hover:text-white hover:bg-slate-800">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-term-job={term.job || "Job"}>
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 bg-slate-900 dark:bg-slate-900 border-r border-slate-800 flex-col z-30">
        {sidebar}
      </aside>
      <div className="lg:pl-60 flex flex-col min-h-screen">
        <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-20">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"
                className="lg:hidden text-slate-300 hover:text-white hover:bg-slate-800">
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-slate-900 border-slate-800">
              {sidebar}
            </SheetContent>
          </Sheet>
          <GlobalSearch />
          <div className="flex items-center gap-1 ml-auto">
            <NotificationsBell />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
        {!!user?.org?.modules?.ai_assistant && <AiAssistant />}
      </div>
    </div>
  );
}
